import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { propTypes } from '../../util/types';
import {
  buildCitySubpageSchema,
  extractFaqsFromBlocks,
} from '../../util/seoDiscoverySchema';
import SeoBreadcrumbs from '../../components/SeoDiscovery/SeoBreadcrumbs';
import SeoContentSections from '../../components/SeoDiscovery/SeoContentSections';
import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import SeoFaqSection from '../../components/SeoDiscovery/SeoFaqSection';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';

const rootUrl = () =>
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

const cityLabel = city => [city?.city, city?.state].filter(Boolean).join(', ');

export const SeoDiscoveryCitySubpageComponent = props => {
  const { payload, fetchInProgress, fetchError, staticContext } = props;

  if (fetchError?.status === 404 && staticContext) {
    staticContext.notfound = true;
  }

  if (fetchInProgress && !payload) {
    return null;
  }

  const city = payload?.city;
  if (!city && fetchError?.status === 404) {
    return (
      <Page title="City page not found | PromoTix">
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <h1 className={css.h1}>City page not found</h1>
            <p>
              <a href="/event-discovery/cities/">Browse all cities</a>
            </p>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (!city) {
    return null;
  }

  const {
    venues = [],
    upcomingEvents = [],
    contentBlocks = [],
    internalLinks = [],
    breadcrumbs = [],
    meta = {},
    page,
    pageKey,
    subpageSlug,
  } = payload;
  const locationLabel = city.locationLabel || cityLabel(city);
  const noIndex = page?.indexState && page.indexState !== 'index';
  const isEvents = pageKey === 'upcoming-events' || subpageSlug === 'events';
  const pageTitle =
    meta.title || meta.metaTitle || `${meta.h1 || city.city} | PromoTix`;
  const pageDescription =
    meta.description ||
    meta.metaDescription ||
    `Find tickets and venues in ${locationLabel} on PromoTix.`;
  const faqs = extractFaqsFromBlocks(contentBlocks);
  const cityHubPath = `/event-discovery/cities/${city.citySlug || ''}/`;
  const canonicalPath =
    page?.canonicalPath ||
    (isEvents ? `${cityHubPath}events/` : `${cityHubPath}venues/`);
  const schema = buildCitySubpageSchema({
    city,
    cityHubPath,
    canonicalPath,
    rootUrl: rootUrl(),
    breadcrumbs,
    faqs,
    venues,
    upcomingEvents,
    meta: {
      ...meta,
      metaTitle: pageTitle,
      metaDescription: pageDescription,
    },
    pageKey: isEvents ? 'upcoming-events' : 'venues',
  });

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      schema={schema}
    >
      {noIndex ? (
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      ) : null}
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {breadcrumbs.length ? <SeoBreadcrumbs items={breadcrumbs} /> : null}
          <h1 className={css.h1}>{meta.h1}</h1>
          <SeoContentSections blocks={contentBlocks} excludeHero />
          {internalLinks.filter(l => l.placement === 'breadcrumb' || l.placement === 'hub_nav')
            .length ? (
            <nav className={css.section} aria-label="City navigation">
              <ul className={css.indexList}>
                {internalLinks
                  .filter(l => l.placement === 'breadcrumb' || l.placement === 'hub_nav')
                  .map(l => (
                    <li key={l.linkId || l.targetPath}>
                      <a href={l.targetPath}>{l.anchorText}</a>
                    </li>
                  ))}
              </ul>
            </nav>
          ) : null}
          {isEvents ? (
            <SeoUpcomingEvents
              events={upcomingEvents}
              title={`Upcoming Events in ${city.city}`}
              emptyMessage={`There are no live listings in ${locationLabel} right now.`}
            />
          ) : (
            <section className={css.section}>
              <h2>Venues in {city.city}</h2>
              {venues.length ? (
                <ul className={css.indexList}>
                  {venues.map(v => (
                    <li key={v.venueId}>
                      <a href={v.path}>{v.venueName}</a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No launched venues listed yet.</p>
              )}
            </section>
          )}
          <SeoFaqSection blocks={contentBlocks} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryCitySubpageComponent.propTypes = {
  payload: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
  staticContext: object,
};

const mapStateToProps = state => state.SeoDiscoveryCitySubpage || {};

export default compose(withRouter, connect(mapStateToProps))(SeoDiscoveryCitySubpageComponent);
